import { ETAResult } from '../lib/api'

interface ETADisplayProps {
  eta: ETAResult
  serviceLevel: 'BASIC' | 'FULL'
}

const confidenceColors = {
  high: 'text-green-600 bg-green-100',
  medium: 'text-yellow-600 bg-yellow-100',
  low: 'text-red-600 bg-red-100',
}

export default function ETADisplay({ eta, serviceLevel }: ETADisplayProps) {
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    return `${hours}h ${mins}m`
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            ETA - {serviceLevel}
          </h3>
          <p className="text-sm text-gray-500">
            Estimated time for new jobs
          </p>
        </div>
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            confidenceColors[eta.confidence]
          }`}
        >
          {eta.confidence} confidence
        </span>
      </div>

      <div className="text-center py-4">
        <p className="text-4xl font-bold text-brand-600">
          {formatDuration(eta.estimatedSeconds)}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          Completion: {new Date(eta.estimatedComplete).toLocaleTimeString()}
        </p>
      </div>

      <div className="border-t pt-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Queue Position</span>
          <span className="font-medium">#{eta.queuePosition}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Active Workers</span>
          <span className="font-medium">{eta.activeWorkers}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Queue Delay</span>
          <span className="font-medium">{formatDuration(eta.breakdown.queueDelaySeconds)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Processing Time</span>
          <span className="font-medium">{formatDuration(eta.breakdown.processingSeconds)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Based On</span>
          <span className="font-medium capitalize">{eta.breakdown.basedOn}</span>
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-4 text-center">
        Updated: {new Date(eta.calculatedAt).toLocaleTimeString()}
      </p>
    </div>
  )
}
